import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, ModalBody, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import { Camera, Edit, Image, Plus, Trash2 } from "react-feather";
import classNames from "classnames";

import { CloseButton } from "components/CloseButton";
import { priceFormat } from "utils/format";
import { getSupplyProductImageFiles } from "../supplyProductFiles";

import "./supplyProductMobileList.scss";

/**
 * Список товаров поступления для узких экранов: карточки вместо таблицы.
 * Фото: камера (getUserMedia, как на списке поступлений для штрихкода) или галерея.
 */
export const SupplyProductMobileList = ({
  productsWithDetails = [],
  status,
  onEdit,
  onDelete,
  onPhotoFiles,
  onViewPhotos,
}) => {
  const fileInputRef = useRef(null);
  const uploadTargetIdRef = useRef(null);
  const photoVideoRef = useRef(null);
  const photoStreamRef = useRef(null);
  const [actionRow, setActionRow] = useState(null);
  const [photoSourceModalOpen, setPhotoSourceModalOpen] = useState(false);
  const [photoCameraMode, setPhotoCameraMode] = useState(false);

  const canUsePhotoCamera =
    typeof navigator !== "undefined" &&
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function";

  const closeActionMenu = () => setActionRow(null);

  const stopPhotoCameraStream = useCallback(() => {
    const stream = photoStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      photoStreamRef.current = null;
    }
    const el = photoVideoRef.current;
    if (el) {
      el.srcObject = null;
    }
  }, []);

  const closePhotoSourceModal = useCallback(() => {
    stopPhotoCameraStream();
    setPhotoSourceModalOpen(false);
    setPhotoCameraMode(false);
  }, [stopPhotoCameraStream]);

  useEffect(() => {
    if (!photoSourceModalOpen || !photoCameraMode) {
      return undefined;
    }
    let active = true;
    let stream = null;
    const videoEl = photoVideoRef.current;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        photoStreamRef.current = stream;
        const el = photoVideoRef.current;
        if (el) {
          el.srcObject = stream;
          await el.play();
        }
      } catch (e) {
        console.warn(e);
        if (active) {
          toast.error(
            "Камера недоступна. Разрешите доступ в настройках браузера или выберите «Из галереи»."
          );
          setPhotoCameraMode(false);
        }
      }
    })();
    return () => {
      active = false;
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
      photoStreamRef.current = null;
      if (videoEl) {
        videoEl.srcObject = null;
      }
    };
  }, [photoSourceModalOpen, photoCameraMode]);

  useEffect(
    () => () => {
      stopPhotoCameraStream();
    },
    [stopPhotoCameraStream]
  );

  const openPhotoSourceModal = (productId) => {
    uploadTargetIdRef.current = productId;
    setPhotoCameraMode(false);
    setPhotoSourceModalOpen(true);
  };

  const openPhotoGalleryPicker = () => {
    closePhotoSourceModal();
    window.setTimeout(() => fileInputRef.current?.click(), 150);
  };

  const openPhotoCameraInModal = () => {
    if (!canUsePhotoCamera) {
      toast.error("Камера в этом браузере недоступна. Выберите «Из галереи».");
      return;
    }
    setPhotoCameraMode(true);
  };

  const capturePhotoFromCamera = () => {
    const video = photoVideoRef.current;
    if (!video || video.readyState < 2 || !video.videoWidth) {
      toast.error("Камера ещё не готова, подождите секунду.");
      return;
    }
    const productId = uploadTargetIdRef.current;
    if (!productId || !onPhotoFiles) {
      closePhotoSourceModal();
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        closePhotoSourceModal();
        uploadTargetIdRef.current = null;
        if (!blob) {
          toast.error("Не удалось снять кадр.");
          return;
        }
        const file = new File([blob], "supply-product-camera.jpg", {
          type: "image/jpeg",
        });
        onPhotoFiles(productId, [file]);
      },
      "image/jpeg",
      0.92
    );
  };

  const backFromPhotoCamera = () => {
    stopPhotoCameraStream();
    setPhotoCameraMode(false);
  };

  const onFileChange = (e) => {
    const picked = Array.from(e.target.files ?? []);
    const productId = uploadTargetIdRef.current;
    e.target.value = "";
    uploadTargetIdRef.current = null;
    if (!productId || !picked.length || !onPhotoFiles) {
      return;
    }
    onPhotoFiles(productId, picked);
  };

  if (!productsWithDetails.length) {
    return (
      <div className="supply-product-mobile-list d-lg-none">
        <div className="supply-product-mobile-list__empty">
          Нет позиций в поступлении
        </div>
      </div>
    );
  }

  const totalCount = productsWithDetails.reduce(
    (acc, row) => acc + (parseInt(row.count, 10) || 0),
    0
  );
  const totalSumm = productsWithDetails.reduce(
    (acc, row) => acc + (Number(row.summ) || 0),
    0
  );

  return (
    <div className="supply-product-mobile-list d-lg-none">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="d-none"
        aria-hidden
        onChange={onFileChange}
      />

      {productsWithDetails.map((row) => {
        const brand = row.nomenclature?.brand ?? row.brand ?? "";
        const article = row.nomenclature?.article ?? row.article ?? "";
        const name = row.nomenclature?.name ?? row.name ?? "";
        const hasNomenclature = Boolean(row.nomenclature?.id);
        const countMismatch =
          status?.alias !== "new" && row.count !== row.factCount;

        return (
          <div
            key={row.id}
            className={classNames(
              "supply-product-mobile-list__card",
              "supply-product-mobile-list__card--tappable",
              {
                "supply-product-mobile-list__card--error": !hasNomenclature,
                "supply-product-mobile-list__card--warn":
                  hasNomenclature && countMismatch,
              }
            )}
            role="button"
            tabIndex={0}
            onClick={() => setActionRow(row)}
            onKeyDown={(e) => {
              if (e.key !== "Enter" && e.key !== " ") {
                return;
              }
              e.preventDefault();
              setActionRow(row);
            }}
          >
            <div className="supply-product-mobile-list__card-main">
              <div className="supply-product-mobile-list__card-text">
                <div
                  className="supply-product-mobile-list__line"
                  title={`${brand} ${article}`.trim()}
                >
                  <strong>{brand}</strong>
                  {brand && article ? " · " : null}
                  {article}
                </div>
                <div
                  className="supply-product-mobile-list__line supply-product-mobile-list__line--muted"
                  title={name}
                >
                  {name || "—"}
                </div>
              </div>
              <div className="supply-product-mobile-list__meta">
                <div className="supply-product-mobile-list__meta-values">
                  <div className="supply-product-mobile-list__meta-price">
                    {priceFormat(row.price)}
                  </div>
                  <div className="supply-product-mobile-list__meta-qty">
                    {row.count ?? "—"}
                  </div>
                  <div className="supply-product-mobile-list__meta-sum">
                    {priceFormat(row.summ)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <Modal isOpen={!!actionRow} toggle={closeActionMenu} centered>
        <ModalHeader className="supply-product-mobile-list__modal-header">
          <span
            className="supply-product-mobile-list__modal-header-text text-truncate"
            title={
              actionRow
                ? `${actionRow.nomenclature?.brand ?? actionRow.brand ?? ""} · ${
                    actionRow.nomenclature?.article ?? actionRow.article ?? ""
                  }`.trim()
                : ""
            }
          >
            {actionRow
              ? [
                  actionRow.nomenclature?.brand ?? actionRow.brand ?? "",
                  actionRow.nomenclature?.article ?? actionRow.article ?? "",
                ]
                  .filter(Boolean)
                  .join(" · ") || "Позиция"
              : ""}
          </span>
          <CloseButton onClick={closeActionMenu} />
        </ModalHeader>
        <ModalBody className="supply-product-mobile-list__modal-body">
          {onEdit && (
            <Button
              block
              className="supply-product-mobile-list__modal-btn"
              color="secondary"
              onClick={() => {
                if (!actionRow) {
                  return;
                }
                onEdit(actionRow);
                closeActionMenu();
              }}
            >
              <Edit size={16} className="mr-50" />
              Изменить
            </Button>
          )}
          {onDelete && (
            <Button
              block
              className="supply-product-mobile-list__modal-btn"
              color="danger"
              outline
              onClick={() => {
                if (!actionRow) {
                  return;
                }
                onDelete(actionRow.id);
                closeActionMenu();
              }}
            >
              <Trash2 size={16} className="mr-50" />
              Удалить
            </Button>
          )}
          {onPhotoFiles && (
            <Button
              block
              className="supply-product-mobile-list__modal-btn supply-product-mobile-list__modal-btn--photo"
              color="primary"
              onClick={() => {
                if (!actionRow) {
                  return;
                }
                const id = actionRow.id;
                closeActionMenu();
                openPhotoSourceModal(id);
              }}
            >
              <Plus size={18} strokeWidth={2.5} className="mr-50" />
              Добавить фото
            </Button>
          )}
          {actionRow &&
            getSupplyProductImageFiles(actionRow).length > 0 &&
            onViewPhotos && (
              <Button
                block
                className="supply-product-mobile-list__modal-btn"
                color="info"
                outline
                onClick={() => {
                  if (!actionRow) {
                    return;
                  }
                  onViewPhotos(actionRow);
                  closeActionMenu();
                }}
              >
                <Image size={16} className="mr-50" />
                Просмотр ({getSupplyProductImageFiles(actionRow).length})
              </Button>
            )}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={photoSourceModalOpen}
        toggle={closePhotoSourceModal}
        centered
        className="supply-product-mobile-list__photo-source-modal"
      >
        <ModalHeader className="supply-product-mobile-list__photo-source-modal-header">
          <span className="supply-product-mobile-list__photo-source-modal-title">
            {photoCameraMode ? "Снимите фото" : "Камера или галерея"}
          </span>
          <CloseButton onClick={closePhotoSourceModal} />
        </ModalHeader>
        <ModalBody>
          {photoCameraMode ? (
            <>
              <video
                ref={photoVideoRef}
                className="supply-product-mobile-list__photo-video"
                playsInline
                muted
                autoPlay
              />
              <div className="supply-product-mobile-list__photo-camera-actions">
                <Button color="primary" onClick={capturePhotoFromCamera}>
                  <Camera size={16} className="mr-50" />
                  Снять фото
                </Button>
                <Button color="secondary" outline onClick={backFromPhotoCamera}>
                  Назад
                </Button>
              </div>
            </>
          ) : (
            <>
              {canUsePhotoCamera ? (
                <Button
                  color="primary"
                  block
                  className="mb-2"
                  onClick={openPhotoCameraInModal}
                >
                  <Camera size={16} className="mr-50" />
                  Снять камерой
                </Button>
              ) : null}
              <Button
                color="secondary"
                outline
                block
                onClick={openPhotoGalleryPicker}
              >
                <Image size={16} className="mr-50" />
                Из галереи
              </Button>
            </>
          )}
        </ModalBody>
      </Modal>

      <div className="supply-product-mobile-list__footer">
        <span>Всего: {totalCount} шт.</span>
        <span>Сумма: {totalSumm ? priceFormat(totalSumm) : "—"}</span>
      </div>
    </div>
  );
};
